// What both the server and the screens need about TimeBlocker: limits and the
// shapes the REST answers have. No dependencies besides the Cases' shared
// types, so the screen imports it as `@trameria/core/timeblocker-shared`.
import type { CaseDayAction, CaseStatus } from "../cases/shared";

/** A time block is at least this long and moves in steps of it (minutes). */
export const BLOCK_MIN_MINUTES = 15;
/** And at most a day long. */
export const BLOCK_MAX_MINUTES = 24 * 60;
/** An event written from the timeline is at least this long (minutes). */
export const EVENT_MIN_MINUTES = 15;
export const LANE_NAME_MAX = 60;
export const EVENT_TITLE_MAX = 1000;

export interface LinkTarget {
  caseId: string;
  stepId?: string | null;
}
export interface TimeBlockLink {
  caseId: string;
  stepId: string | null;
  caseTitle: string;
  stepTitle: string | null;
  status: CaseStatus;
}
/** A part of a Block spent on one Case or Action (open while `endedAt` is null). */
export interface BlockWork {
  caseId: string;
  stepId: string | null;
  startedAt: string;
  endedAt: string | null;
}
export interface TimeBlock {
  id: string;
  title: string | null;
  startAt: string;
  endAt: string;
  startedAt: string | null;
  endedAt: string | null;
  /**
   * The plan as it stood when its start came: fixed then, while `startAt` and
   * `endAt` keep what it was moved to since. Null while the start is ahead.
   */
  committedStartAt: string | null;
  committedEndAt: string | null;
  links: TimeBlockLink[];
  /** What was worked on inside the Block, switched by hand (oldest first). */
  works: BlockWork[];
}
export interface TimeBlockerDay {
  actions: CaseDayAction[];
  blocks: TimeBlock[];
}
export interface TimeBlockerNow {
  running: TimeBlock | null;
  next: TimeBlock | null;
}

/** What moved in trameria while a block ran: a close, or a chat's run. */
export interface BlockActivity {
  kind: "step_closed" | "case_closed" | "agent_run";
  caseId: string | null;
  stepId: string | null;
  /** The Action's, the Case's or the chat's title. */
  title: string;
  caseTitle: string | null;
  /** The chat of an `agent_run`. */
  chatId: string | null;
  at: string;
  /** Whether the block is linked to it (its Case as a whole, or the Action). */
  linked: boolean;
}
/**
 * What became of a block: pressed (`started`), not pressed but something moved
 * in its time (`activity`, as planned), nothing (`none`), or not over yet
 * (`upcoming`).
 */
export type BlockDid = "started" | "activity" | "none" | "upcoming";
/**
 * How a block was changed after its start came (its committed plan against
 * what it is now). Null when it was not.
 */
export interface BlockChange {
  /** Now start minus committed start. */
  shiftMs: number;
  /** Now length minus committed length (negative: shortened). */
  lengthMs: number;
  /** Moved to a later day (in the review's time zone). */
  deferred: boolean;
}
export interface ReviewBlock {
  block: TimeBlock;
  did: BlockDid;
  actualMs: number;
  /** The committed length, else (start still ahead) the length now. */
  plannedMs: number;
  /** Whether its plan lies in the range: a block planned before it and moved in has no plan here. */
  planned: boolean;
  change: BlockChange | null;
  /** Linked Cases and Actions closed within the block. */
  done: number;
  activity: BlockActivity[];
}
export interface TimeBlockerReview {
  /** The plans of the range (committed ones), those still to come too. */
  plannedMs: number;
  actualMs: number;
  /** The rates count only planned blocks whose plan is over: these are their sums. */
  overPlannedMs: number;
  overActualMs: number;
  blocks: number;
  didBlocks: number;
  doneCount: number;
  /** Planned blocks changed after their start came, by how. */
  movedBlocks: number;
  shortenedBlocks: number;
  deferredBlocks: number;
  items: ReviewBlock[];
}

/** The plan a block is reviewed against: the committed one, else the one now. */
export function blockPlan(block: TimeBlock): {
  startAt: string;
  endAt: string;
} {
  return block.committedStartAt && block.committedEndAt
    ? { startAt: block.committedStartAt, endAt: block.committedEndAt }
    : { startAt: block.startAt, endAt: block.endAt };
}

/** What a block is called: its title, else what it is linked to first. */
export function blockLabel(block: Pick<TimeBlock, "title" | "links">): string {
  return (
    block.title?.trim() ||
    block.links[0]?.stepTitle ||
    block.links[0]?.caseTitle ||
    "Block"
  );
}

/** The screen of a block (its detail open), and with `start` it starts it. */
export function blockPath(id: string, action?: "start"): string {
  const q = new URLSearchParams({ block: id });
  if (action) q.set("action", action);
  return `/me/timeblocker?${q}`;
}

// ---- calendars ------------------------------------------------------------------

export interface CalendarAccount {
  id: string;
  email: string;
}

/** A lane the owner added (the own lane is not one: its id is null). */
export interface CalendarLane {
  id: string;
  name: string;
  position: number;
}

export interface CalendarSource {
  id: string;
  accountId: string;
  name: string;
  /** The color shown (Google's until the owner picks one). */
  color: string | null;
  googleColor: string | null;
  colorOverridden: boolean;
  accessRole: string;
  writable: boolean;
  shown: boolean;
  /** Null: the own lane. */
  laneId: string | null;
}

export interface CalendarSettings {
  /** Whether this app can connect Google at all (an OAuth client is set). */
  googleAvailable: boolean;
  accounts: CalendarAccount[];
  lanes: CalendarLane[];
  sources: CalendarSource[];
}

export interface CalendarEvent {
  id: string;
  sourceId: string;
  /** Null: the own lane. */
  laneId: string | null;
  title: string;
  /** Timed events: ISO times. All-day events: null. */
  startAt: string | null;
  endAt: string | null;
  /** All-day events: YYYY-MM-DD, the end exclusive (Google's). */
  startDate: string | null;
  endDate: string | null;
  location: string | null;
  description: string | null;
  meetingUrl: string | null;
  htmlLink: string | null;
  etag: string | null;
  recurring: boolean;
}

export interface CalendarEvents {
  events: CalendarEvent[];
  /** Calendars that could not be read this time (the others are shown). */
  errors: { sourceId: string; code: string }[];
}

/** The code of a 409 when the event changed on Google's side since it was read. */
export const CALENDAR_CONFLICT = "calendar_conflict";
/** The code when no Google account is connected (or it stopped working). */
export const GOOGLE_NOT_CONNECTED = "google_not_connected";

export interface GoogleConnection {
  id: string;
  email: string;
}
